// 1. Import your custom baseApi (This forces it to use Axios and your live URL!)
import { baseApi } from '@/lib/api/baseApi';
import type {
    ThreatOverviewResponse,
    IntelActivityResponse,
    ThreatFeedPostsResponse,
    ThreatActorDirectoryResponse,
    PublicNewsResponse,           // <-- Added for Public News
    PublicNewsActivityResponse    // <-- Added for Public News Activity
} from '../api/threatFeedsTypes';

// 2. We INJECT the endpoints into the existing baseApi instead of creating a new one
export const threatFeedsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // 1. Fetches the entire Overview Dashboard on load
        getThreatOverview: builder.query<ThreatOverviewResponse, void>({
            query: () => ({
                url: '/threat-feeds/overview/',
                method: 'GET',
            }),
            // Use tags to allow for auto-refetching/caching if you configure them in baseApi
            providesTags: ['ThreatSummary', 'DashboardStats'] as any,
        }),

        // 2. Fetches just the timeline data when user clicks 7D, 30D, or 90D
        getIntelActivity: builder.query<IntelActivityResponse, 7 | 30 | 90>({
            query: (days) => ({
                url: `/threat-feeds/intel-activity/`,
                method: 'GET',
                // Using the params object ensures Axios formats the ?dates= URL correctly
                params: { dates: days }
            }),
        }),

        // 3. Fetches the Dark Web Chatters feed with optional filtering
        getThreatFeedPosts: builder.query<ThreatFeedPostsResponse, Record<string, any> | void>({
            query: (params) => ({
                url: '/threat-feeds/posts/',
                method: 'GET',
                // Automatically passes page, limit, start_date, search, category, etc.
                params,
            }),
        }),

        // 4. Fetches the Threat Actor Directory with optional filtering
        getThreatActorDirectory: builder.query<ThreatActorDirectoryResponse, Record<string, any> | void>({
            query: (params) => ({
                url: '/threat-feeds/threat-actor-directory/',
                method: 'GET',
                params,
            }),
        }),

        // 5. Fetches Public News list with optional filtering
        getPublicNews: builder.query<PublicNewsResponse, Record<string, any>>({
            query: (params) => ({
                url: '/threat-feeds/public-news/',
                method: 'GET',
                params,
            }),
        }),

        // 6. Fetches Public News Activity for the Bar Chart
        getPublicNewsActivity: builder.query<PublicNewsActivityResponse, Record<string, any>>({
            query: (params) => ({
                url: '/threat-feeds/public-news-activity/',
                method: 'GET',
                params,
            }),
        }),
    }),
    overrideExisting: true,
});

export const {
    useGetThreatOverviewQuery,
    useGetIntelActivityQuery,
    useLazyGetIntelActivityQuery, // Using lazy query so we can trigger it manually on button clicks
    useGetThreatFeedPostsQuery,   // Exporting the hook for the Chatters page
    useGetThreatActorDirectoryQuery, // Exporting the hook for the Directory page
    useGetPublicNewsQuery,         // <-- Exporting the hook for Public News
    useGetPublicNewsActivityQuery  // <-- Exporting the hook for Public News Activity
} = threatFeedsApi;